/**
 * theme.js — Theme + appearance manager
 * Persists choice in localStorage; respects system preference on first visit.
 */
const STORAGE_KEY = "sz_theme_v1";
const PREFS_KEY   = "sz_theme_prefs_v1";

// ── Default appearance preferences ───────────────────────────────────────────
const DEFAULTS = {
  accent:       "teal",      // teal | blue | purple | orange | rose | indigo
  radius:       "normal",    // tight | normal | rounded
  density:      "comfortable", // compact | comfortable | spacious
  sidebar:      "expanded",  // expanded | collapsed
  animations:   true,
  blur:         true,
};

export const ACCENT_PRESETS = {
  teal:   { name: "WhatsApp",  c: "#128C7E", h: "#0e7163", p: "#0b5c51", sub: "#e8f5f3", soft: "#c8eae6", text: "#0a4f46", focus: "rgba(18,140,126,.55)", focusRing: "rgba(18,140,126,.20)" },
  blue:   { name: "Oceano",    c: "#2563eb", h: "#1d4ed8", p: "#1e40af", sub: "#eff6ff", soft: "#dbeafe", text: "#1e3a8a", focus: "rgba(37,99,235,.55)",  focusRing: "rgba(37,99,235,.20)"  },
  purple: { name: "Lavanda",   c: "#7c3aed", h: "#6d28d9", p: "#5b21b6", sub: "#faf5ff", soft: "#e9d5ff", text: "#4c1d95", focus: "rgba(124,58,237,.55)", focusRing: "rgba(124,58,237,.20)" },
  orange: { name: "Sunset",    c: "#ea580c", h: "#c2410c", p: "#9a3412", sub: "#fff7ed", soft: "#ffedd5", text: "#7c2d12", focus: "rgba(234,88,12,.55)",  focusRing: "rgba(234,88,12,.20)"  },
  rose:   { name: "Rosé",      c: "#e11d48", h: "#be123c", p: "#9f1239", sub: "#fff1f2", soft: "#ffe4e6", text: "#881337", focus: "rgba(225,29,72,.55)",  focusRing: "rgba(225,29,72,.20)"  },
  indigo: { name: "Noite",     c: "#4f46e5", h: "#4338ca", p: "#3730a3", sub: "#eef2ff", soft: "#e0e7ff", text: "#312e81", focus: "rgba(79,70,229,.55)",  focusRing: "rgba(79,70,229,.20)"  },
};

export const RADIUS_PRESETS = {
  tight:   { xs: "2px",  sm: "4px",  md: "6px",  lg: "8px",  xl: "12px" },
  normal:  { xs: "4px",  sm: "8px",  md: "12px", lg: "16px", xl: "20px" },
  rounded: { xs: "6px",  sm: "12px", md: "16px", lg: "20px", xl: "24px" },
};

export const DENSITY_PRESETS = {
  compact:     { ctrlH: "30px", ctrlHsm: "26px", ctrlHlg: "36px", textBase: "12px" },
  comfortable: { ctrlH: "36px", ctrlHsm: "30px", ctrlHlg: "42px", textBase: "13px" },
  spacious:    { ctrlH: "42px", ctrlHsm: "36px", ctrlHlg: "48px", textBase: "14px" },
};

// ── Light/Dark theme ─────────────────────────────────────────────────────────
export function getInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {}
  // Safe Zap v2.0 — dark é a identidade primária (Linear/Vercel style)
  // Light fica como opt-in. Mesmo que o sistema do usuário esteja em light,
  // mostramos dark por padrão na primeira visita.
  return "dark";
}

export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  html.dataset.theme = theme;
  if (!html.classList.contains("theme-ready")) {
    requestAnimationFrame(() => html.classList.add("theme-ready"));
  }
}

export function saveTheme(theme) {
  try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
}

export function toggleTheme(currentTheme) {
  const next = currentTheme === "dark" ? "light" : "dark";
  applyTheme(next);
  saveTheme(next);
  return next;
}

// ── Appearance preferences ──────────────────────────────────────────────────
export function getInitialPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULTS };
}

export function savePrefs(prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
}

export function applyPrefs(prefs) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const merged = { ...DEFAULTS, ...prefs };

  // 1) Accent color
  const accent = ACCENT_PRESETS[merged.accent] || ACCENT_PRESETS.teal;
  html.style.setProperty("--accent", accent.c);
  html.style.setProperty("--accent-hover", accent.h);
  html.style.setProperty("--accent-press", accent.p);
  html.style.setProperty("--accent-subtle", accent.sub);
  html.style.setProperty("--accent-soft", accent.soft);
  html.style.setProperty("--accent-text", accent.text);
  html.style.setProperty("--border-focus", accent.focus);
  html.style.setProperty("--shadow-focus", `0 0 0 3px ${accent.focusRing}`);

  // 2) Border radius
  const r = RADIUS_PRESETS[merged.radius] || RADIUS_PRESETS.normal;
  html.style.setProperty("--r-xs", r.xs);
  html.style.setProperty("--r-sm", r.sm);
  html.style.setProperty("--r", r.md);
  html.style.setProperty("--r-md", r.md);
  html.style.setProperty("--r-lg", r.lg);
  html.style.setProperty("--r-xl", r.xl);

  // 3) Density
  const d = DENSITY_PRESETS[merged.density] || DENSITY_PRESETS.comfortable;
  html.style.setProperty("--ctrl-h", d.ctrlH);
  html.style.setProperty("--ctrl-h-sm", d.ctrlHsm);
  html.style.setProperty("--ctrl-h-lg", d.ctrlHlg);
  html.style.setProperty("--text-base", d.textBase);

  // 4) Data attributes for sidebar / animations / blur
  html.dataset.sidebar    = merged.sidebar;
  html.dataset.animations = merged.animations ? "on" : "off";
  html.dataset.blur       = merged.blur ? "on" : "off";
}

export function updatePref(key, value) {
  const prefs = { ...getInitialPrefs(), [key]: value };
  savePrefs(prefs);
  applyPrefs(prefs);
  return prefs;
}

export function resetPrefs() {
  savePrefs(DEFAULTS);
  applyPrefs(DEFAULTS);
  return { ...DEFAULTS };
}

// Bootstrap before React mounts (call from index.js)
export function bootstrapTheme() {
  applyTheme(getInitialTheme());
  applyPrefs(getInitialPrefs());
}
